import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetSupplier, useGetSupplierServices } from '~/hooks/apis/tours/useTourSuppliers';

import { costPriceScopeLabel, costPriceTypeLabel } from '~/types/tours/supplier';

import ServiceFormDialog from '../../Suppliers/ServiceFormDialog';
import { SUPPLIERS_PATH } from '../../nativeTourModel';

export default function TourSupplierPage() {
  const { supplier_id: supplierId } = useParams<{ supplier_id: string }>();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: supplier, isLoading, error } = useGetSupplier(supplierId);
  const {
    data: services = [],
    isLoading: isLoadingServices,
    error: servicesError,
  } = useGetSupplierServices(supplierId);

  return (
    <Container>
      <PageHeader title={supplier?.name ?? 'Supplier'} backButton={SUPPLIERS_PATH}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)} disabled={!supplier}>
          Create service
        </Button>
      </PageHeader>

      <Stack spacing={2}>
        {isLoading && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {!!error && <Alert severity="error">{error.message}</Alert>}

        {!!supplier && (
          <>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {supplier.email} · {supplier.phone} · {supplier.address}
              </Typography>
            </Stack>

            {isLoadingServices && (
              <Box p={4} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            )}

            {!!servicesError && <Alert severity="error">{servicesError.message}</Alert>}

            {!isLoadingServices && !servicesError && !services.length && (
              <Alert severity="info">
                This supplier has no services yet. Create one against a tour to link the tour to this supplier.
              </Alert>
            )}

            {!!services.length && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Cost price</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Configurations</TableCell>
                    <TableCell>Package options</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service) => (
                    <TableRow key={service.id} hover>
                      <TableCell>{service.name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={service.kind} />
                      </TableCell>
                      <TableCell>{costPriceTypeLabel(service.costPriceType)}</TableCell>
                      <TableCell>{costPriceScopeLabel(service.costPriceScope)}</TableCell>
                      <TableCell>
                        {service.configurations.map((configuration) => configuration.name).join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        {service.configurations.reduce(
                          (count, configuration) => count + configuration.packages.length,
                          0,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Stack>

      {isCreateOpen && !!supplier && (
        <ServiceFormDialog open={isCreateOpen} supplierId={supplier.id} onClose={() => setIsCreateOpen(false)} />
      )}
    </Container>
  );
}
